// ─── Chin Off Engine ──────────────────────────────────────
// Real-time limit-order engine triggered by the "Off" toggle.
//
// When a user sets the Off button to BUY or SELL, this engine
// monitors tick-by-tick prices and fires orders only when price
// reaches the configured trigger level.
//
// Logic:
//   BUY setup:
//     - If current price < trigger → auto-off immediately
//       (price already below trigger, opportunity missed)
//     - If current price > trigger → monitor continuously.
//       When price drops to ≤ trigger → check risk → place orders
//
//   SELL setup:
//     - If current price > trigger → auto-off immediately
//       (price already above trigger, opportunity missed)
//     - If current price < trigger → monitor continuously.
//       When price rises to ≥ trigger → check risk → place orders
//
// After price triggers, the order placement loop (risk check,
// volume conversion, confirmation polling, max orders) is
// delegated to startOrderLoop from chin-order-executor.ts.
//
// ABORT: Uses AbortController for immediate shutdown. Once abort()
// is called, price monitoring stops AND the inner order loop
// (if running) is also aborted instantly.

import { startOrderLoop } from '@/lib/chin-order-executor';
import type { OrderLoopStatus, PlaceOrderResult } from '@/lib/chin-order-executor';

// ─── Types ──────────────────────────────────────────────────

export interface OffEngineConfig {
  accountId: string;
  symbolId: number;
  symbolName: string;
  direction: 'buy' | 'sell';
  conditionPrice: number;
  volume: number;
  maxOrders: number;
}

export type OffEnginePhase =
  | 'monitoring'      // Watching price, waiting for trigger
  | 'auto_off'        // Price already passed trigger → deactivated
  | 'triggered'       // Price hit trigger, starting order checks
  | 'ordering'        // Order loop is running (delegated)
  | 'completed'       // All orders placed successfully
  | 'blocked'         // Risk control blocked
  | 'error';          // Unexpected error

export interface OffEngineStatus {
  symbolId: number;
  phase: OffEnginePhase;
  currentPrice: number | null;
  conditionPrice: number;
  message: string;
  ordersPlaced: number;
  maxOrders: number;
}

export interface OffEngineCallbacks {
  /** Get the current bid price (returns null if no data yet) */
  getCurrentBid: () => number | null;
  /** Check if risk control allows placing an order */
  checkRisk: () => { allowed: boolean; reason: string };
  /** Place a market order via cTrader */
  placeOrder: (params: {
    symbolId: number;
    tradeSide: 'BUY' | 'SELL';
    volume: number;
  }) => Promise<PlaceOrderResult>;
  /** Get current open position count for this symbol */
  getPositionCount: () => Promise<number>;
  /** Called when engine auto-offs (price already passed trigger) */
  onAutoOff: (symbolId: number) => void;
}

// ─── Constants ──────────────────────────────────────────────

const PRICE_POLL_INTERVAL = 200; // 200ms between price checks
const MAX_PRICE_WAIT_ATTEMPTS = 50; // 10 seconds max for initial price

// ─── Engine ─────────────────────────────────────────────────

export function startOffEngine(
  config: OffEngineConfig,
  callbacks: OffEngineCallbacks,
  onStatus: (status: OffEngineStatus) => void,
): { abort: () => void } {
  let aborted = false;
  const abortController = new AbortController();
  const { signal } = abortController;

  // Track inner order loop so we can abort it too
  let orderLoopAbort: (() => void) | null = null;

  const emit = (overrides: Partial<OffEngineStatus>) => {
    if (aborted) return;
    onStatus({
      symbolId: config.symbolId,
      phase: 'monitoring',
      currentPrice: null,
      conditionPrice: config.conditionPrice,
      message: '',
      ordersPlaced: 0,
      maxOrders: config.maxOrders,
      ...overrides,
    });
  };

  /**
   * Sleep that resolves IMMEDIATELY when abort() is called.
   */
  const sleep = (ms: number): Promise<void> => {
    if (aborted) return Promise.resolve();
    return new Promise((resolve) => {
      if (signal.aborted) {
        resolve();
        return;
      }
      const timer = setTimeout(resolve, ms);
      const onAbort = () => {
        clearTimeout(timer);
        resolve();
      };
      signal.addEventListener('abort', onAbort, { once: true });
    });
  };

  // ─── Wait for order loop completion ─────────────────────
  // startOrderLoop is fire-and-forget. We wrap it in a Promise
  // that resolves when the loop finishes (completed / max_reached / error).

  function runOrderLoop(): Promise<{ ordersPlaced: number; finalPhase: string }> {
    return new Promise((resolve) => {
      const orderConfig = {
        accountId: config.accountId,
        symbolId: config.symbolId,
        symbolName: config.symbolName,
        direction: config.direction,
        volume: config.volume,
        maxOrders: config.maxOrders,
      };

      let lastPlaced = 0;

      const { abort } = startOrderLoop(
        orderConfig,
        {
          checkRisk: callbacks.checkRisk,
          placeOrder: callbacks.placeOrder,
          getPositionCount: callbacks.getPositionCount,
        },
        (orderStatus: OrderLoopStatus) => {
          if (aborted) return;

          lastPlaced = orderStatus.ordersPlaced;

          // Forward order status as off-engine status
          const phaseMap: Record<string, OffEnginePhase> = {
            checking_risk: 'ordering',
            placing_order: 'ordering',
            waiting_confirmation: 'ordering',
            idle: 'ordering',
            completed: 'completed',
            max_reached: 'completed',
            blocked: 'blocked',
            error: 'error',
          };

          emit({
            phase: phaseMap[orderStatus.phase] || 'ordering',
            ordersPlaced: orderStatus.ordersPlaced,
            currentPrice: callbacks.getCurrentBid(),
            message: orderStatus.message || orderStatus.phase.replace('_', ' '),
          });

          // Detect completion
          if (
            orderStatus.phase === 'completed' ||
            orderStatus.phase === 'max_reached' ||
            orderStatus.phase === 'error'
          ) {
            resolve({
              ordersPlaced: orderStatus.ordersPlaced,
              finalPhase: orderStatus.phase,
            });
          }
        },
      );

      orderLoopAbort = abort;
    });
  }

  // ─── Main Run ────────────────────────────────────────────

  async function run(): Promise<void> {
    const directionLabel = config.direction.toUpperCase();

    console.log(
      `[OffEngine] 🎯 Starting: ${config.symbolName} ${directionLabel} ` +
      `trigger=${config.conditionPrice} vol=${config.volume} max=${config.maxOrders}`
    );

    // ── Step 1: Wait for initial price data ──
    let bid: number | null = null;
    let attempts = 0;

    while (bid === null && !aborted && attempts < MAX_PRICE_WAIT_ATTEMPTS) {
      bid = callbacks.getCurrentBid();
      if (bid === null) {
        if (attempts === 0) {
          emit({
            phase: 'monitoring',
            message: 'Waiting for price data...',
          });
        }
        await sleep(PRICE_POLL_INTERVAL);
        attempts++;
      }
    }

    if (aborted) return;

    if (bid === null) {
      emit({
        phase: 'error',
        message: 'No price data available after 10s — check connection',
      });
      console.warn(`[OffEngine] ❌ ${config.symbolName}: No price data`);
      return;
    }

    console.log(
      `[OffEngine] 📊 ${config.symbolName}: initial bid=${bid}, ` +
      `trigger=${config.conditionPrice}, direction=${directionLabel}`
    );

    // ── Step 2: Immediate check — has price already passed? ──
    if (config.direction === 'buy') {
      if (bid < config.conditionPrice) {
        console.log(
          `[OffEngine] ⚠️ ${config.symbolName}: bid ${bid} < trigger ${config.conditionPrice} → AUTO-OFF`
        );
        emit({
          phase: 'auto_off',
          currentPrice: bid,
          message: `Price ${bid} already below trigger ${config.conditionPrice} — auto OFF`,
        });
        callbacks.onAutoOff(config.symbolId);
        return;
      }

      // ── Step 3: Monitor — wait for bid ≤ trigger ──
      emit({
        phase: 'monitoring',
        currentPrice: bid,
        message: `BUY: waiting for price ≤ ${config.conditionPrice}`,
      });

      console.log(
        `[OffEngine] 👀 ${config.symbolName}: monitoring BUY, ` +
        `current=${bid} > trigger=${config.conditionPrice}, waiting for drop...`
      );

      while (!aborted) {
        bid = callbacks.getCurrentBid();
        if (bid !== null && bid <= config.conditionPrice) {
          console.log(
            `[OffEngine] 🔥 ${config.symbolName}: TRIGGERED! ` +
            `bid=${bid} ≤ trigger=${config.conditionPrice}`
          );
          break;
        }
        if (bid !== null) {
          emit({
            phase: 'monitoring',
            currentPrice: bid,
            message: `BUY waiting (bid=${bid}, target≤${config.conditionPrice})`,
          });
        }
        await sleep(PRICE_POLL_INTERVAL);
      }
    } else {
      // SELL direction
      if (bid > config.conditionPrice) {
        console.log(
          `[OffEngine] ⚠️ ${config.symbolName}: bid ${bid} > trigger ${config.conditionPrice} → AUTO-OFF`
        );
        emit({
          phase: 'auto_off',
          currentPrice: bid,
          message: `Price ${bid} already above trigger ${config.conditionPrice} — auto OFF`,
        });
        callbacks.onAutoOff(config.symbolId);
        return;
      }

      // ── Step 3: Monitor — wait for bid ≥ trigger ──
      emit({
        phase: 'monitoring',
        currentPrice: bid,
        message: `SELL: waiting for price ≥ ${config.conditionPrice}`,
      });

      console.log(
        `[OffEngine] 👀 ${config.symbolName}: monitoring SELL, ` +
        `current=${bid} < trigger=${config.conditionPrice}, waiting for rise...`
      );

      while (!aborted) {
        bid = callbacks.getCurrentBid();
        if (bid !== null && bid >= config.conditionPrice) {
          console.log(
            `[OffEngine] 🔥 ${config.symbolName}: TRIGGERED! ` +
            `bid=${bid} ≥ trigger=${config.conditionPrice}`
          );
          break;
        }
        if (bid !== null) {
          emit({
            phase: 'monitoring',
            currentPrice: bid,
            message: `SELL waiting (bid=${bid}, target≥${config.conditionPrice})`,
          });
        }
        await sleep(PRICE_POLL_INTERVAL);
      }
    }

    if (aborted) {
      console.log(`[OffEngine] 🛑 Aborted during monitoring: ${config.symbolName}`);
      return;
    }

    // ── Step 4: Price triggered! Check risk then run order loop ──
    emit({
      phase: 'triggered',
      currentPrice: bid,
      message: `PRICE HIT! bid=${bid}, checking risk...`,
    });

    console.log(
      `[OffEngine] 🚀 ${config.symbolName}: Price hit, starting order loop...`
    );

    try {
      const result = await runOrderLoop();

      if (aborted) {
        console.log(`[OffEngine] 🛑 Aborted during order loop: ${config.symbolName}`);
        return;
      }

      console.log(
        `[OffEngine] ✅ ${config.symbolName}: Order loop finished — ` +
        `${result.ordersPlaced} orders placed (phase=${result.finalPhase})`
      );

      emit({
        phase: 'completed',
        currentPrice: callbacks.getCurrentBid(),
        ordersPlaced: result.ordersPlaced,
        message: `Done: ${result.ordersPlaced} order(s) placed`,
      });
    } catch (err) {
      if (aborted) return;
      console.error(`[OffEngine] ❌ ${config.symbolName}: Order loop error:`, err);
      emit({
        phase: 'error',
        currentPrice: callbacks.getCurrentBid(),
        message: `Order loop failed: ${String(err)}`,
      });
    }
  }

  // Fire and forget
  run();

  return {
    abort: () => {
      if (aborted) return;
      aborted = true;
      abortController.abort();
      // Also abort the inner order loop if running
      if (orderLoopAbort) {
        orderLoopAbort();
        orderLoopAbort = null;
      }
      console.log(`[OffEngine] 🛑 Abort signal sent for ${config.symbolName}`);
    },
  };
}